import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getSelectCarrierAdvertisementSuccessExp } from 'app/api/cookie';
import { useGetUser, useIsPayee, usePayeeDetails } from 'app/api/hooks/core/v1/auth/user';
import AriaClickHandler from 'app/components/v2/components/AriaClickHandler';
import Flex from 'app/components/v2/components/Flex';
import { payeeRoutes, routes } from 'app/constants/routes';
import { AnalyticsEventTriggerOnMount, useAnalytics } from 'app/services/analytics';
import { gaTrackEvent } from 'app/services/google-analytics';
import { useGeneralState } from 'app/state/GeneralContext/hooks';

import LargeSelectCarrierBadge from './LargeSelectCarrierBadge';
import SmallSelectCarrierBadge from './SmallSelectCarrierBadge';

// Component Definition
const PayeeSelectCarrierBadge: FC = () => {
  const { t } = useTranslation('components');
  const { isSideNavOpen } = useGeneralState();
  const user = useGetUser();
  const payeeDetails = usePayeeDetails();
  const isPayee = useIsPayee();

  const navigate = useNavigate();
  const reportAnalytics = useAnalytics();

  if (!user?.company?.id) {
    return null;
  }

  const isSelectCarrier = isPayee &&
    (!!payeeDetails?.isRateAccepted || !!getSelectCarrierAdvertisementSuccessExp(user.company.id));

  const handleNavigateToSelectCarrier = () => {
    reportAnalytics({ eventType: 'selectCarrierNavbarLinkClicked' });
    gaTrackEvent('selectCarrierNavbarLinkClicked');
    navigate(`/${routes.payee}/${payeeRoutes.selectCarrier}`);
  };

  if (!isPayee) {
    return null;
  }

  const badge = (
    <Flex
      alignItems={isSideNavOpen ? 'center' : 'flex-start'}
      flexDirection="column"
      fullWidth
      marginTop={15}
      padding={4}
    >
      {isSideNavOpen
        ? (
          <LargeSelectCarrierBadge isBadgeFilledIn={isSelectCarrier} />
        )
        : (
          <SmallSelectCarrierBadge />
        )}
    </Flex>
  );

  return isSelectCarrier
    ? (
      badge
    )
    : (
      <AnalyticsEventTriggerOnMount
        analyticsEvent={{
          eventType: 'selectCarrierNavbarLinkVisible',
        }}
        gaEventKey="selectCarrierNavbarLinkVisible"
      >
        <AriaClickHandler
          ariaLabel={t('payeeSelectCarrierBadge.clickAria', 'Navigate to select carrier information.')}
          onClick={handleNavigateToSelectCarrier}
          role="link"
        >
          {badge}
        </AriaClickHandler>
      </AnalyticsEventTriggerOnMount>
    );
};

export default PayeeSelectCarrierBadge;
